import React, { useEffect, useState } from "react"
import { getGroupTheme } from "../../utils/groupTheme"

const FREQUENCY_OPTIONS = [
  "Daily",
  "Bi-Daily",
  "Weekly",
  "Bi-Weekly",
  "Monthly",
  "Bi-Monthly",
  "Quarterly",
  "Bi-Annually",
  "Annually",
]

function GroupChamaaCyclePanel({
  group,
  isStartingCycle = false,
  isPausingGroup = false,
  isResumingGroup = false,
  isStoppingGroup = false,
  onStartCycle,
  onPauseGroup,
  onResumeGroup,
  onStopGroup,
  readOnly = false,
}) {
  const theme = getGroupTheme(group)
  const [startDate, setStartDate] = useState("")
  const [invoiceFrequency, setInvoiceFrequency] = useState("Bi-Weekly")
  const [contributionAmount, setContributionAmount] = useState("")
  const [maxSharesPerMember, setMaxSharesPerMember] = useState("1")
  const [selectedMemberIds, setSelectedMemberIds] = useState([])
  const [localError, setLocalError] = useState("")
  const [localSuccess, setLocalSuccess] = useState("")
  const [isFormOpen, setIsFormOpen] = useState(true)

  const members = Array.isArray(group?.members) ? group.members : []
  const activeCycle = group?.activeCycle || null
  const lifecycleState = String(group?.chamaaLifecycle?.state || "running").toLowerCase()
  const isPaused = lifecycleState === "paused"
  const isStopped = lifecycleState === "stopped"
  const isRunning = !isPaused && !isStopped
  const canStartNewCycle =
    isRunning &&
    (!activeCycle || activeCycle.status === "completed" || activeCycle.status === "cancelled")

  useEffect(() => {
    if (!group?.id) return
    setLocalError("")
    setLocalSuccess("")
    setSelectedMemberIds(members.map((m) => String(m.id)))
    const defaultContributionAmount =
      group?.nextCycle?.contributionAmount ??
      group?.activeCycle?.contributionAmount ??
      group?.amountPerPerson ??
      ""
    setContributionAmount(String(defaultContributionAmount))
    setMaxSharesPerMember(String(group?.maxSharesPerMember || 1))
    setInvoiceFrequency("Bi-Weekly")
    // Auto-collapse form if cycle is active
    if (activeCycle && activeCycle.status !== "completed") {
      setIsFormOpen(false)
    }
  }, [group?.id, group?.amountPerPerson, group?.activeCycle?.contributionAmount, group?.nextCycle?.contributionAmount, group?.maxSharesPerMember]) // eslint-disable-line react-hooks/exhaustive-deps

  const selectableMembers = members.map((m) => ({
    id: String(m.id),
    name: m.name || "Member",
    membershipStatus: m.membershipStatus || "none",
  }))

  const toggleMember = (memberId) => {
    setSelectedMemberIds((prev) => {
      if (prev.includes(memberId)) return prev.filter((id) => id !== memberId)
      return [...prev, memberId]
    })
  }

  const handleStartCycle = async (e) => {
    e.preventDefault()
    if (readOnly) return
    setLocalError("")
    setLocalSuccess("")

    if (!startDate) {
      setLocalError("Start date is required.")
      return
    }
    if (!invoiceFrequency) {
      setLocalError("Invoice frequency is required.")
      return
    }
    const amount = Number(contributionAmount)
    if (!Number.isFinite(amount) || amount <= 0) {
      setLocalError("Contribution amount must be greater than 0.")
      return
    }
    if (selectedMemberIds.length === 0) {
      setLocalError("Select at least one member for this cycle.")
      return
    }
    const maxShares = Number(maxSharesPerMember || 1)
    if (!Number.isFinite(maxShares) || maxShares < 1) {
      setLocalError("Max shares per person must be at least 1.")
      return
    }

    const payload = {
      startDate,
      invoiceFrequency,
      contributionAmount: amount,
      includedMemberIds: selectedMemberIds,
      maxSharesPerMember: maxShares,
    }

    const result = await onStartCycle?.(payload)
    if (!result?.ok) {
      setLocalError(result?.message || "Failed to start cycle.")
      return
    }

    setLocalSuccess(result?.message || "Cycle started successfully.")
    setIsFormOpen(false)
  }

  const runLifecycleAction = async (runner, successMessage, fallbackErrorMessage) => {
    if (readOnly) return
    setLocalError("")
    setLocalSuccess("")
    const result = await runner?.()
    if (!result?.ok) {
      setLocalError(result?.message || fallbackErrorMessage)
      return
    }
    setLocalSuccess(result?.message || successMessage)
  }

  const handlePauseGroup = async () => {
    await runLifecycleAction(
      () => onPauseGroup?.(),
      "Chamaa paused.",
      "Failed to pause Chamaa."
    )
  }

  const handleResumeGroup = async () => {
    await runLifecycleAction(
      () => onResumeGroup?.(),
      "Chamaa resumed.",
      "Failed to resume Chamaa."
    )
  }

  const handleStopGroup = async () => {
    const confirmed = window.confirm(
      "Stop this Chamaa permanently? The current round will be completed first, next cycle drafts will be deleted, and resume will not be possible."
    )
    if (!confirmed) return
    await runLifecycleAction(
      () => onStopGroup?.(),
      "Chamaa stopped permanently.",
      "Failed to stop Chamaa."
    )
  }

  const isLifecycleActionBusy =
    !!isPausingGroup || !!isResumingGroup || !!isStoppingGroup || !!isStartingCycle

  return (
    <div className="space-y-4">
      {readOnly && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Archived view is read-only. Reactivate the group to edit cycle settings.
        </div>
      )}
      <fieldset
        disabled={readOnly}
        className={`min-w-0 border-0 p-0 m-0 space-y-4 ${readOnly ? "opacity-75" : ""}`}
      >
      <div className="bg-white/95 rounded-2xl border border-gray-200 shadow-md p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Chamaa Lifecycle</p>
            <p className="text-[11px] text-gray-500 mt-1">
              {isStopped
                ? "Stopped permanently. New cycles are disabled."
                : isPaused
                  ? "Paused. Cycle processing is frozen until resumed."
                  : "Running. Normal cycle processing is active."}
            </p>
          </div>
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
              isStopped
                ? "bg-gray-100 text-gray-700 border-gray-200"
                : isPaused
                  ? "bg-slate-100 text-slate-700 border-slate-200"
                  : "bg-emerald-100 text-emerald-700 border-emerald-200"
            }`}
          >
            {isStopped ? "Stopped" : isPaused ? "Paused" : "Running"}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isStopped && isRunning && (
            <button
              type="button"
              onClick={handlePauseGroup}
              disabled={isLifecycleActionBusy}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border disabled:opacity-50 transition-colors ${theme.bg} ${theme.text} ${theme.border} hover:opacity-90`}
            >
              {isPausingGroup ? "Pausing..." : "Pause"}
            </button>
          )}
          {!isStopped && isPaused && (
            <button
              type="button"
              onClick={handleResumeGroup}
              disabled={isLifecycleActionBusy}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide text-white disabled:opacity-60 shadow-sm transition-all ${theme.button}`}
            >
              {isResumingGroup ? "Resuming..." : "Resume"}
            </button>
          )}
          {!isStopped && (
            <button
              type="button"
              onClick={handleStopGroup}
              disabled={isLifecycleActionBusy}
              className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 disabled:opacity-50 transition-colors"
            >
              {isStoppingGroup ? "Stopping..." : "Stop Permanently"}
            </button>
          )}
        </div>
      </div>

      {!activeCycle && isRunning && (
        <div className="bg-white/95 rounded-2xl border border-gray-200 shadow-md p-4">
          <p className="text-xs text-gray-500">
            Configure and start a new merry-go-round cycle below.
          </p>
        </div>
      )}

      {canStartNewCycle && (
        <div className={`border border-gray-200 rounded-xl overflow-hidden transition-all duration-300 ${isFormOpen ? 'bg-gray-50' : 'bg-white shadow-md'}`}>
          <button
            type="button"
            onClick={() => setIsFormOpen(!isFormOpen)}
            className="w-full flex items-center justify-between p-3 text-left"
          >
            <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Start New Cycle</span>
            <span className="text-gray-400 text-xs">{isFormOpen ? "Hide" : "Show"}</span>
          </button>

          {isFormOpen && (
            <form onSubmit={handleStartCycle} className="p-3 border-t border-gray-200 space-y-3 animate-in slide-in-from-top-2 bg-white">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 ${theme.ring} outline-none`}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">Invoice Frequency</label>
                  <select
                    value={invoiceFrequency}
                    onChange={(e) => setInvoiceFrequency(e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 ${theme.ring} outline-none bg-white`}
                  >
                    {FREQUENCY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">Contribution per Member</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={contributionAmount}
                    onChange={(e) => setContributionAmount(e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 ${theme.ring} outline-none`}
                    placeholder="e.g. 1000"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">Max Shares per Member</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={maxSharesPerMember}
                    onChange={(e) => setMaxSharesPerMember(e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 ${theme.ring} outline-none`}
                    placeholder="e.g. 2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">Included Members</label>
                <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg bg-white divide-y divide-gray-50">
                  {selectableMembers.map((member) => (
                    <label key={member.id} className="flex items-center justify-between px-3 py-2 text-xs hover:bg-gray-50 cursor-pointer">
                      <span className="text-gray-700 font-medium">
                        {member.name}
                      </span>
                      <input
                        type="checkbox"
                        checked={selectedMemberIds.includes(member.id)}
                        onChange={() => toggleMember(member.id)}
                        className={`rounded text-${theme.primary} ${theme.ring}`}
                      />
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={isStartingCycle}
                  className={`px-6 py-2 rounded-xl text-xs font-bold uppercase tracking-wide disabled:opacity-60 shadow-md transform transition active:scale-95 ${theme.button}`}
                >
                  {isStartingCycle ? "Starting..." : "Start Cycle"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {(localError || localSuccess) && (
        <div className="space-y-2">
          {!!localError && (
            <div className="border border-red-300 bg-red-50 text-red-700 text-xs p-2 rounded-lg shadow-sm">
              {localError}
            </div>
          )}
          {!!localSuccess && (
            <div className="border border-green-300 bg-green-50 text-green-700 text-xs p-2 rounded-lg shadow-sm">
              {localSuccess}
            </div>
          )}
        </div>
      )}
      </fieldset>
    </div>
  )
}

export default GroupChamaaCyclePanel
